// PlateCorrectionEditor — Desktop v1 of the in-app plate-georef correction tool.
//
// Lets any authenticated user fix the georeferencing of LC + failed approach
// plates by tapping waypoint positions on the plan-view chart.
//
// Workflow (boss-approved 2026-05-16 05:35 PT):
//   1. User picks a waypoint from the right-side checklist (CIFP fixes,
//      navaids, or the special "Airport (ARP)" entry).
//   2. Cursor becomes a red crosshair on the plate raster.
//   3. User clicks where that waypoint appears on the chart.
//   4. Red pin lands; can be dragged to refine, right-clicked to remove.
//   5. Optional: draw the FAC arrow (2 clicks: outer end → runway side).
//      The arrow is visual orientation today; tomorrow it'll be a true
//      rotation constraint for the solver (Phase 2).
//   6. With ≥2 placed anchors → "Save correction" → POST /api/plates/correction.
//
// Boss flow (2026-05-18 evening, final): on a failed plate the
// cartographer types the airport ident, the editor resolves the ARP
// lat/lon and offers "Airport (ARP)" as a pinnable waypoint. Place the
// airport pin + one navaid/fix pin + draw the FAC arrow = enough to
// geolocate. Most plates are north-up so 2 pins suffice; the FAC arrow
// is used as a sanity check today and a real rotation constraint later.
//
// Desktop-only v1. Mobile users get a notice.

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';

// ME-10 (audit-synthesis-2026-05-24 § HI-3'): consolidated with
// src/map/plateOverlay/renderPlateBitmap.ts into a shared loader.
import { loadPdfjs } from '../lib/pdfjs';

/**
 * EditorFix — a waypoint that can be placed as an anchor on the plate.
 *
 * The `source` field drives the tabbed checklist UX so the cartographer
 * understands provenance:
 *   - 'cifp'             — published CIFP procedure leg
 *   - 'plate-text'       — extracted from the PDF text layer by the
 *                          NASR suggester (high-confidence: ident is
 *                          named on the plate AND resolved in NASR)
 *   - 'geographic-nearby'— NASR navaid/fix within 25 NM of the ARP,
 *                          offered as a fallback when text extraction
 *                          yielded too few hits. Lower confidence — the
 *                          cartographer is responsible for matching it
 *                          to the plate's symbology
 *   - 'manual'           — typed into the manual-entry input and
 *                          resolved via /api/nasr/lookup
 */
export interface EditorFix {
  ident: string;
  lat: number;
  lon: number;
  /** 'faf' | 'map' | 'iaf' | 'navaid' | 'tick' | 'custom' — informational. */
  type?: string;
  source: 'cifp' | 'plate-text' | 'geographic-nearby' | 'manual';
  /** NASR navaid kind (VOR, VORTAC, NDB, ...) for non-CIFP sources. */
  navaidType?: string;
  /** Human-readable name from NASR (e.g. 'MONTAGUE'). */
  name?: string;
  /** Distance from ARP in NM, populated for NASR-sourced fixes. */
  distanceFromArpNm?: number;
  /** True bearing from ARP, populated for NASR-sourced fixes. */
  bearingFromArpDeg?: number;
  /** Best-effort role inferred from plate text context. */
  inferredRole?: 'iaf' | 'faf' | 'map' | 'navaid' | 'unknown';
  /** True if the user has already placed an anchor for this waypoint. */
  placed?: boolean;
}

/**
 * Back-compat alias for call sites still using the pre-suggester type
 * name. New code should use `EditorFix`. Callers that build a CifpFix
 * without a `source` field will land in the 'cifp' tab by default —
 * see the runtime fallback in the checklist render.
 */
export type CifpFix = EditorFix;

interface Anchor {
  waypoint: string;
  /** Page-point coordinates (in the PDF's native point space, top-left origin). */
  px: number;
  py: number;
  lat: number;
  lon: number;
  anchorType?: string;
}

interface CoursePoint {
  /** Page-point coordinates. */
  px: number;
  py: number;
}



interface Props {
  cycle: string;
  pdfName: string;
  /** The airport ident actually used for CIFP / NASR lookups. Falls
   *  back to 'UNKN' when no ident is known. The wrapper passes
   *  effectiveAirport = override ?? row.airport ?? null. */
  airport: string;
  /** The original ident on the queue row (may be null when the
   *  upstream pipeline couldn't identify the plate's airport). When
   *  null and no override is set, the editor renders an
   *  airport-override input so the cartographer can type what they
   *  read from the top of the plate. (2026-05-18 boss request.) */
  airportFromRow?: string | null;
  /** Called when the cartographer confirms an airport ident from the
   *  override input. The wrapper re-runs CIFP + suggester fetches
   *  with the new ident. */
  onAirportOverride?: (ident: string) => void;
  procedureIdent?: string;
  /** Waypoints from any source — CIFP, NASR-suggested, geographic, manual. */
  fixes: EditorFix[];
  /** True while the wrapper is re-fetching CIFP + NASR for a new airport
   *  override. Drives a small spinner on the override banner so the
   *  cartographer sees that the lookup is in flight. */
  fixesLoading?: boolean;
  /** Suggester mode hint for the banner copy. */
  suggesterMode?: 'text-primary' | 'text+geographic' | 'geographic-only' | null;
  /** Course in degrees true (for the snap-to-course-line feature). */
  courseDegTrue?: number;
  onClose: () => void;
  onSaved?: () => void;
}

interface SolveResult {
  avgResidualNm: number;
  matchCount: number;
  model: string;
  corners: [[number, number], [number, number], [number, number], [number, number]];
  pageCorners: [[number, number], [number, number], [number, number], [number, number]];
}

const PIN_RADIUS = 8;
const PIN_HIT_RADIUS = 14;
const COURSE_SNAP_PX = 10;

export function PlateCorrectionEditor({
  cycle,
  pdfName,
  airport,
  airportFromRow,
  onAirportOverride,
  procedureIdent,
  fixes,
  fixesLoading,
  suggesterMode,
  courseDegTrue,
  onClose,
  onSaved,
}: Props) {
  // True only when the queue row never had an airport ident attached.
  // Used to gate the override-input banner. Distinct from
  // `airport === 'UNKN'` so we don't accidentally show the banner once
  // the cartographer has confirmed an override.
  const airportUnknown = !airportFromRow;
  // ── Plate raster state ───────────────────────────────────────────────
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [pageSize, setPageSize] = useState<{ w: number; h: number } | null>(null);
  const [renderScale, setRenderScale] = useState<number>(1);
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Zoom + pan (CSS-level; we render the PDF once at a fixed scale).
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const dragRef = useRef<{ x: number; y: number; panX0: number; panY0: number } | null>(null);

  // ── Anchors + course line ────────────────────────────────────────────
  // Manual-entry fixes — hoisted above the click handler so the
  // handler can reference it. (2026-05-18: manually-typed waypoints
  // couldn't be placed because the click handler only scanned the
  // prop `fixes`, missing manual additions entirely.)
  const [manualFixes, setManualFixes] = useState<EditorFix[]>([]);

  const [anchors, setAnchors] = useState<Anchor[]>([]);
  const [placingWaypoint, setPlacingWaypoint] = useState<string | null>(null);
  const [coursePoints, setCoursePoints] = useState<CoursePoint[]>([]);
  const [drawingCourse, setDrawingCourse] = useState<boolean>(false);
  const [draggingAnchorIdx, setDraggingAnchorIdx] = useState<number | null>(null);

  // ── Solver + save state ──────────────────────────────────────────────
  const [solveResult, setSolveResult] = useState<SolveResult | null>(null);
  const [solving, setSolving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  // ── Geometric fingerprint (simple raster hash) ───────────────────────
  const [fingerprint, setFingerprint] = useState<string | null>(null);

  // Load the PDF and render page 1 to canvas.
  useEffect(() => {
    let cancelled = false;
    setLoadingPdf(true);
    setPdfError(null);

    const pdfUrl = `/api/plates/pdf?cycle=${encodeURIComponent(cycle)}&pdf=${encodeURIComponent(pdfName)}`;
    (async () => {
      try {
        const resp = await fetch(pdfUrl);
        if (!resp.ok) throw new Error(`PDF fetch ${resp.status}`);
        const buf = await resp.arrayBuffer();
        if (cancelled) return;
        const pdfjsLib = await loadPdfjs();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        const page = await pdf.getPage(1);
        // Render at scale=3 for sharp pin placement; cap pixel count.
        const MAX_PIXELS = 24_000_000;
        let scale = 3;
        let vp = page.getViewport({ scale });
        while (vp.width * vp.height > MAX_PIXELS && scale > 1) {
          scale -= 0.5;
          vp = page.getViewport({ scale });
        }
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = vp.width;
        canvas.height = vp.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas ctx unavailable');
        await page.render({ canvas, canvasContext: ctx, viewport: vp }).promise;
        if (cancelled) return;
        // The native PDF page is at scale=1; remember the ratio so we can
        // map mouse → page-point coords.
        const nativeVp = page.getViewport({ scale: 1 });
        setPageSize({ w: nativeVp.width, h: nativeVp.height });
        setRenderScale(scale);
        // Auto-fit: pick an initial zoom so the rendered canvas fills the
        // left container. Then center vertically only — leave panX at 0
        // so the plate hugs the left edge of the container (boss
        // 2026-05-19 21:40 PT: plate was ending up too small and shifted
        // toward the middle/right when we centered horizontally).
        const container = containerRef.current;
        if (container) {
          const rect = container.getBoundingClientRect();
          const fitW = (rect.width - 16) / vp.width;   // small right-edge margin only
          const fitH = (rect.height - 32) / vp.height; // 16 top + 16 bottom margin
          const fit = Math.max(0.05, Math.min(1, Math.min(fitW, fitH)));
          setZoom(fit);
          const scaledH = vp.height * fit;
          setPanX(0);
          setPanY(Math.max(0, (rect.height - scaledH) / 2));
        }
        // Compute a lightweight fingerprint over the rendered raster.
        try {
          const fp = await computeRasterFingerprint(canvas);
          if (!cancelled) setFingerprint(fp);
        } catch { /* fingerprint optional */ }
        setLoadingPdf(false);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setPdfError(msg);
        setLoadingPdf(false);
      }
    })();

    return () => { cancelled = true; };
  }, [cycle, pdfName]);

  // Convert a mouse event on the container to page-point coords.
  const eventToPagePt = useCallback(
    (e: React.MouseEvent | MouseEvent): { px: number; py: number; canvasX: number; canvasY: number } | null => {
      const canvas = canvasRef.current;
      if (!canvas || !pageSize) return null;
      const rect = canvas.getBoundingClientRect();
      const canvasX = ((e.clientX - rect.left) / rect.width) * canvas.width;
      const canvasY = ((e.clientY - rect.top) / rect.height) * canvas.height;
      const px = canvasX / renderScale;
      const py = canvasY / renderScale;
      return { px, py, canvasX, canvasY };
    },
    [pageSize, renderScale],
  );

  // Snap a candidate point to the approach-course line if it's within range.
  const maybeSnapToCourse = useCallback(
    (px: number, py: number): { px: number; py: number } => {
      if (coursePoints.length !== 2) return { px, py };
      const [a, b] = coursePoints;
      const dx = b.px - a.px;
      const dy = b.py - a.py;
      const len2 = dx * dx + dy * dy;
      if (len2 < 1e-9) return { px, py };
      // Project (px,py) onto segment a→b (clamped 0..1).
      const t = Math.max(0, Math.min(1, ((px - a.px) * dx + (py - a.py) * dy) / len2));
      const sx = a.px + t * dx;
      const sy = a.py + t * dy;
      const distPagePt = Math.hypot(sx - px, sy - py);
      // Convert COURSE_SNAP_PX (canvas pixels) → page-points
      const snapPagePt = COURSE_SNAP_PX / renderScale;
      if (distPagePt <= snapPagePt) return { px: sx, py: sy };
      return { px, py };
    },
    [coursePoints, renderScale],
  );

  // Handle click on the canvas.
  const onCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      if (loadingPdf || !pageSize) return;
      const pt = eventToPagePt(e);
      if (!pt) return;

      if (drawingCourse) {
        // Course-line draw mode. Click 1 = origin, click 2 = arrow tip.
        setCoursePoints((cp) => {
          if (cp.length >= 2) return [{ px: pt.px, py: pt.py }];
          return [...cp, { px: pt.px, py: pt.py }];
        });
        if (coursePoints.length === 1) {
          setDrawingCourse(false);
        }
        return;
      }

      if (placingWaypoint) {
        // (2026-05-18 boss report) The lookup used to scan only the prop
        // `fixes` array, which excludes manualFixes the user just typed
        // into the Manual entry tab. Result: the user could see the
        // 'Place' button on a manual waypoint, click it, click the
        // plate — and nothing happened (silent no-op). Now we scan
        // BOTH lists so manually-typed waypoints are placeable.
        const fix =
          fixes.find((f) => f.ident === placingWaypoint)
          ?? manualFixes.find((f) => f.ident === placingWaypoint);
        if (!fix) return;
        // Tick anchors get snapped; others place as-clicked.
        const snapped = (fix.type === 'tick' || fix.type === 'navaid')
          ? maybeSnapToCourse(pt.px, pt.py)
          : { px: pt.px, py: pt.py };
        const newAnchor: Anchor = {
          waypoint: fix.ident,
          px: snapped.px,
          py: snapped.py,
          lat: fix.lat,
          lon: fix.lon,
          anchorType: fix.type,
        };
        setAnchors((prev) => {
          // Replace any existing anchor for this waypoint.
          const others = prev.filter((a) => a.waypoint !== fix.ident);
          return [...others, newAnchor];
        });
        setPlacingWaypoint(null);
        setSolveResult(null);
        return;
      }
    },
    [coursePoints.length, drawingCourse, eventToPagePt, fixes, manualFixes, loadingPdf, maybeSnapToCourse, pageSize, placingWaypoint],
  );

  // Pin hit-testing
  const findAnchorAtPoint = useCallback(
    (px: number, py: number): number => {
      if (!pageSize) return -1;
      const hitR = PIN_HIT_RADIUS / renderScale;
      for (let i = 0; i < anchors.length; i++) {
        const a = anchors[i];
        if (Math.hypot(a.px - px, a.py - py) <= hitR) return i;
      }
      return -1;
    },
    [anchors, pageSize, renderScale],
  );

  // Mouse handlers for drag (anchor reposition or pan).
  const onCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const pt = eventToPagePt(e);
      if (!pt) return;
      const idx = findAnchorAtPoint(pt.px, pt.py);
      if (idx >= 0 && !placingWaypoint && !drawingCourse) {
        setDraggingAnchorIdx(idx);
        e.preventDefault();
        return;
      }
      // Otherwise start pan (only when not placing)
      if (!placingWaypoint && !drawingCourse) {
        dragRef.current = { x: e.clientX, y: e.clientY, panX0: panX, panY0: panY };
        e.preventDefault();
      }
    },
    [eventToPagePt, findAnchorAtPoint, placingWaypoint, drawingCourse, panX, panY],
  );

  const onCanvasMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (draggingAnchorIdx !== null) {
        const pt = eventToPagePt(e);
        if (!pt) return;
        setAnchors((prev) => prev.map((a, i) => {
          if (i !== draggingAnchorIdx) return a;
          const snapped = (a.anchorType === 'tick' || a.anchorType === 'navaid')
            ? maybeSnapToCourse(pt.px, pt.py)
            : { px: pt.px, py: pt.py };
          return { ...a, px: snapped.px, py: snapped.py };
        }));
        return;
      }
      if (dragRef.current) {
        const d = dragRef.current;
        setPanX(d.panX0 + (e.clientX - d.x));
        setPanY(d.panY0 + (e.clientY - d.y));
      }
    },
    [draggingAnchorIdx, eventToPagePt, maybeSnapToCourse],
  );

  const onCanvasMouseUp = useCallback(() => {
    if (draggingAnchorIdx !== null) {
      setDraggingAnchorIdx(null);
      setSolveResult(null);
    }
    dragRef.current = null;
  }, [draggingAnchorIdx]);

  // Right-click to remove an anchor.
  const onCanvasContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const pt = eventToPagePt(e);
      if (!pt) return;
      const idx = findAnchorAtPoint(pt.px, pt.py);
      if (idx >= 0) {
        setAnchors((prev) => prev.filter((_, i) => i !== idx));
        setSolveResult(null);
      }
    },
    [eventToPagePt, findAnchorAtPoint],
  );

  // Mouse wheel to zoom (centered on cursor).
  const onCanvasWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const delta = -e.deltaY * 0.001;
      const newZoom = Math.max(0.25, Math.min(8, zoom * (1 + delta)));
      // Adjust pan to zoom around the cursor.
      const container = containerRef.current;
      if (!container) {
        setZoom(newZoom);
        return;
      }
      const rect = container.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const ratio = newZoom / zoom;
      setPanX((px) => mx - (mx - px) * ratio);
      setPanY((py) => my - (my - py) * ratio);
      setZoom(newZoom);
    },
    [zoom],
  );

  // ── FAC arrow sanity check ───────────────────────────────────
  //
  // If the user has drawn the course arrow AND we know the published
  // FAC, compare the arrow's on-page bearing to the expected bearing.
  // On a north-up plate the page-Y axis points DOWN (south), so the
  // pixel bearing is `atan2(dx, -dy)` in standard compass convention
  // (0° = north, increasing clockwise). courseDegTrue is the inbound
  // true bearing TO the airport, which matches the arrow direction
  // (origin → tip).
  //
  // A divergence > 10° usually means one of:
  //   • the arrow was drawn backwards (tip on the outer end)
  //   • the plate isn't north-up (rotated; rare on FAA TPP)
  //   • the course label is wrong
  // We surface this as a soft amber warning, never a hard block.
  const facWarning = useMemo<string | null>(() => {
    if (coursePoints.length !== 2 || courseDegTrue === undefined) return null;
    const [a, b] = coursePoints;
    const dx = b.px - a.px;
    const dy = b.py - a.py;
    if (Math.hypot(dx, dy) < 1e-3) return null;
    // Compass bearing of arrow on page (north-up assumption).
    let arrowDeg = (Math.atan2(dx, -dy) * 180) / Math.PI;
    if (arrowDeg < 0) arrowDeg += 360;
    let diff = Math.abs(arrowDeg - courseDegTrue);
    if (diff > 180) diff = 360 - diff;
    if (diff > 10) {
      return `Arrow points ${arrowDeg.toFixed(0)}° on page; published FAC is ${courseDegTrue.toFixed(0)}°T (Δ ${diff.toFixed(0)}°). Re-draw with the tip toward the airport, or the plate may not be north-up.`;
    }
    return null;
  }, [coursePoints, courseDegTrue]);

  // Solve the affine on the SERVER (we have the canonical port; client doesn't
  // duplicate the math).
  const canSolve = anchors.length >= 2;
  const couldSave = anchors.length >= 2;

  const handleSave = useCallback(async () => {
    if (!pageSize || !couldSave) return;
    if (!supabase) {
      setSaveError('Supabase not configured — sign-in required');
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSolveResult(null);
    // Diagnostic logging — boss reported the editor sticks at 'Saving…'
    // with no confirmation, and the row didn't reach the review queue.
    // Console output lets us see exactly which step fails next time.
    // (2026-05-21 20:42 PDT.)
    console.log('[plate-corr] save: starting', { pdfName, cycle, anchorCount: anchors.length });

    try {
      // Read token synchronously from the auth store — avoids the
      // supabase.auth.getSession() async call which can hang on token
      // refresh (PKCE flow), leaving the spinner stuck forever.
      // Pattern matches PlateOverlayLayer.tsx line 1151.
      const token = useAuthStore.getState().session?.access_token;
      if (!token) {
        console.warn('[plate-corr] save: no session token');
        setSaveError('Not signed in — please log in first');
        setSaving(false);
        return;
      }
      console.log('[plate-corr] save: have token, building body');
      const body = {
        pdfName,
        cycle,
        anchors: anchors.map((a) => ({
          waypoint: a.waypoint,
          px: a.px,
          py: a.py,
          lat: a.lat,
          lon: a.lon,
          anchorType: a.anchorType,
        })),
        plateFingerprint: fingerprint,
        pageSize,
        // Plan-view crop: use the v30 default for now (LC/failed plates often
        // have no published plan-view rect). Top 65% is a safe heuristic.
        planViewRect: { minX: 0.02, minY: 0.05, maxX: 0.98, maxY: 0.62 },
        // Full page in page-point space.
        pageRect: { minX: 0, minY: 0, maxX: pageSize.w, maxY: pageSize.h },
        courseDegTrue,
        airport,
        procedureIdent,
      };
      // 30 s safety timeout — a hanging serverless cold start would otherwise
      // leave the spinner stuck forever with no error message.
      const ac = new AbortController();
      const timeoutId = window.setTimeout(() => ac.abort(), 30_000);
      console.log('[plate-corr] save: POST /api/plates/correction');
      let resp: Response;
      try {
        resp = await fetch('/api/plates/correction', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
          signal: ac.signal,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }
      console.log('[plate-corr] save: response', resp.status, resp.statusText);
      const data = await resp.json().catch(() => ({ ok: false, error: 'Server returned invalid JSON' }));
      console.log('[plate-corr] save: parsed body', data);
      if (!resp.ok || !data.ok) {
        const msg = data.error ?? `HTTP ${resp.status}`;
        console.warn('[plate-corr] save: error path', msg);
        setSaveError(msg);
        setSaving(false);
        return;
      }
      console.log('[plate-corr] save: success', { hasSolve: !!data.solve, hasCorrection: !!data.correction });
      setSolveResult(data.solve ?? null);
      setSavedOk(true);
      setSaving(false);
      onSaved?.();
    } catch (e) {
      const msg = e instanceof Error
        ? (e.name === 'AbortError' ? 'Save timed out after 30s — try again' : e.message)
        : String(e);
      console.error('[plate-corr] save: caught', e);
      setSaveError(msg);
      setSaving(false);
    }
  }, [anchors, airport, couldSave, courseDegTrue, cycle, fingerprint, onSaved, pageSize, pdfName, procedureIdent]);

  // ── Tabbed checklist state ──────────────────────────────────────────────
  // Default tab: first source with entries. CIFP wins if present, else
  // plate-text, else geographic, else manual.
  type ChecklistTab = 'cifp' | 'plate-text' | 'geographic-nearby' | 'manual';
  // manualFixes is hoisted above (see top of component).

  // Annotated fixes list (with placed flag). Includes manual-entry adds.
  const annotatedFixes = useMemo<EditorFix[]>(() => {
    const placedSet = new Set(anchors.map((a) => a.waypoint));
    // Normalize legacy CIFP fixes (no source field) into 'cifp'.
    const merged: EditorFix[] = [
      ...fixes.map((f) => ({ ...f, source: f.source ?? 'cifp' })),
      ...manualFixes,
    ];
    return merged.map((f) => ({ ...f, placed: placedSet.has(f.ident) }));
  }, [fixes, manualFixes, anchors]);

  const fixesBySource = useMemo(() => {
    const m: Record<ChecklistTab, EditorFix[]> = {
      'cifp': [],
      'plate-text': [],
      'geographic-nearby': [],
      'manual': [],
    };
    for (const f of annotatedFixes) m[f.source].push(f);
    return m;
  }, [annotatedFixes]);

  const tabConfigs: Array<{ key: ChecklistTab; label: string }> = [
    { key: 'cifp', label: 'CIFP procedure' },
    { key: 'plate-text', label: 'From plate text' },
    { key: 'geographic-nearby', label: 'Nearby (geographic)' },
    { key: 'manual', label: 'Manual entry' },
  ];

  const defaultTab: ChecklistTab = useMemo(() => {
    if (fixesBySource.cifp.length > 0) return 'cifp';
    if (fixesBySource['plate-text'].length > 0) return 'plate-text';
    if (fixesBySource['geographic-nearby'].length > 0) return 'geographic-nearby';
    return 'manual';
    // Only re-derive when the source counts actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixesBySource.cifp.length, fixesBySource['plate-text'].length,
      fixesBySource['geographic-nearby'].length]);

  const [activeTab, setActiveTab] = useState<ChecklistTab>(defaultTab);
  // Re-sync if defaultTab changes (e.g. CIFP loads asynchronously after open).
  useEffect(() => { setActiveTab(defaultTab); }, [defaultTab]);

  // Banner copy reflects the suggester mode for plates that triggered the
  // fallback (so the cartographer knows whether they're working from
  // extracted idents vs. proximity guesses).
  const bannerText = useMemo<string | null>(() => {
    if (fixesBySource.cifp.length > 0) return null;
    if (suggesterMode === 'geographic-only') {
      return "Plate text didn't yield idents — nearby NASR waypoints offered as a fallback. Verify each candidate against the plate before placing.";
    }
    if (suggesterMode === 'text+geographic') {
      return 'Some anchors extracted from plate text; nearby NASR waypoints also offered.';
    }
    if (suggesterMode === 'text-primary') {
      return 'Anchors suggested from plate text via NASR. Click an ident, then click its position on the plate.';
    }
    if (fixesBySource['plate-text'].length === 0 && fixesBySource['geographic-nearby'].length === 0) {
      return 'No CIFP procedure and no NASR matches. Use the Manual entry tab to look up idents you can see on the plate.';
    }
    return 'Plate-only IAP — no CIFP procedure. NASR-suggested anchors below.';
  }, [fixesBySource.cifp.length, fixesBySource['plate-text'].length,
      fixesBySource['geographic-nearby'].length, suggesterMode]);

  // ── Render ───────────────────────────────────────────────────────────

  // Cursor styling: red crosshair when placing, draw-mode when drawing line,
  // grab when panning.
  const cursorClass = placingWaypoint
    ? 'cursor-crosshair'
    : drawingCourse
      ? 'cursor-crosshair'
      : draggingAnchorIdx !== null
        ? 'cursor-grabbing'
        : 'cursor-grab';

  // Canvas wrapper transform.
  const canvasTransform = `translate(${panX}px, ${panY}px) scale(${zoom})`;

  // Editor frame: CSS Grid with explicit named columns so flex
  // quirks (intrinsic content overflow, parent transform breaking
  // position:fixed, etc.) can't push the plate off the left side.
  //
  // 2026-05-19 21:50 PT root cause: when the editor is launched via
  // deep-link from the corrections queue (`?openPlate=...&autoEditor=1`)
  // it mounts inside AirportDetail's sidebar `<aside>`, which has
  // `backdrop-blur` (CSS `backdrop-filter`). `backdrop-filter` creates
  // a containing block for `position: fixed` descendants in WebKit/
  // Blink, so `fixed inset-0` no longer covers the viewport — it only
  // covers the AirportDetail sidebar region (~30% of the screen, right
  // side). The plate was being squeezed into that narrow area while
  // the rest of the viewport showed the map page behind.
  //
  // Fix: render the editor via a React portal directly under
  // `document.body`, escaping the AirportDetail subtree's containing
  // block. Now `fixed inset-0` covers the actual viewport regardless
  // of which mount point launched it.
  return createPortal(
    <div
      className="fixed inset-0 z-[110] bg-slate-900 text-white"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 360px',
        gridTemplateRows: '1fr',
      }}
    >
      {/* LEFT: plate canvas. grid-column:1 forces the plate to the
           left of the aside no matter what. */}
      <div
        ref={containerRef}
        className={`relative overflow-hidden bg-slate-950 ${cursorClass}`}
        style={{ gridColumn: 1, gridRow: 1, minWidth: 0 }}
        onWheel={onCanvasWheel}
      >
        {loadingPdf && (
          <div className="absolute inset-0 grid place-items-center text-slate-400">
            Loading plate…
          </div>
        )}
        {pdfError && (
          <div className="absolute inset-0 grid place-items-center p-6 text-rose-400">
            Failed to load plate: {pdfError}
          </div>
        )}
        <div
          className="absolute left-0 top-0 origin-top-left"
          style={{ transform: canvasTransform }}
        >
          <canvas
            ref={canvasRef}
            onClick={onCanvasClick}
            onMouseDown={onCanvasMouseDown}
            onMouseMove={onCanvasMouseMove}
            onMouseUp={onCanvasMouseUp}
            onMouseLeave={onCanvasMouseUp}
            onContextMenu={onCanvasContextMenu}
            className="block select-none"
            style={{ imageRendering: 'crisp-edges' }}
          />
          {/* SVG overlay for anchors + course line, sized to the canvas */}
          {pageSize && canvasRef.current && (
            <svg
              className="pointer-events-none absolute left-0 top-0"
              width={canvasRef.current.width}
              height={canvasRef.current.height}
            >
              {coursePoints.length === 2 && (() => {
                const x1 = coursePoints[0].px * renderScale;
                const y1 = coursePoints[0].py * renderScale;
                const x2 = coursePoints[1].px * renderScale;
                const y2 = coursePoints[1].py * renderScale;
                const dx = x2 - x1;
                const dy = y2 - y1;
                const len = Math.hypot(dx, dy) || 1;
                const ux = dx / len;
                const uy = dy / len;
                // Arrowhead at the end (direction of travel: FAF → MAP /
                // runway). Size scales with zoom so it stays readable.
                const arrowLen = 14 / zoom;
                const arrowHalf = 7 / zoom;
                const ax = x2 - ux * arrowLen;
                const ay = y2 - uy * arrowLen;
                // Perpendicular for the arrowhead base spread.
                const px = -uy;
                const py = ux;
                const aLx = ax + px * arrowHalf;
                const aLy = ay + py * arrowHalf;
                const aRx = ax - px * arrowHalf;
                const aRy = ay - py * arrowHalf;
                return (
                  <g>
                    <line
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="cyan"
                      strokeWidth={2 / zoom}
                      strokeDasharray={`${8 / zoom} ${4 / zoom}`}
                      opacity={0.8}
                    />
                    <polygon
                      points={`${x2},${y2} ${aLx},${aLy} ${aRx},${aRy}`}
                      fill="cyan"
                      opacity={0.95}
                    />
                  </g>
                );
              })()}
              {coursePoints.map((cp, i) => (
                <circle
                  key={`cp-${i}`}
                  cx={cp.px * renderScale}
                  cy={cp.py * renderScale}
                  r={4 / zoom}
                  fill="cyan"
                  opacity={0.8}
                />
              ))}
              {anchors.map((a, i) => (
                <g key={`a-${i}`}>
                  <circle
                    cx={a.px * renderScale}
                    cy={a.py * renderScale}
                    r={PIN_RADIUS / zoom}
                    fill="rgba(244,63,94,0.85)"
                    stroke="white"
                    strokeWidth={2 / zoom}
                  />
                  <text
                    x={a.px * renderScale + 12 / zoom}
                    y={a.py * renderScale - 6 / zoom}
                    fontSize={12 / zoom}
                    fill="white"
                    stroke="black"
                    strokeWidth={2 / zoom}
                    paintOrder="stroke"
                  >
                    {a.waypoint}
                  </text>
                </g>
              ))}
            </svg>
          )}
        </div>
        {/* Zoom controls */}
        <div className="absolute bottom-3 left-3 flex gap-2 rounded bg-slate-800/80 p-1 text-xs">
          <button className="rounded px-2 py-1 hover:bg-slate-700" onClick={() => setZoom((z) => Math.min(8, z * 1.2))}>＋</button>
          <button className="rounded px-2 py-1 hover:bg-slate-700" onClick={() => setZoom((z) => Math.max(0.25, z / 1.2))}>－</button>
          <button className="rounded px-2 py-1 hover:bg-slate-700" onClick={() => { setZoom(1); setPanX(0); setPanY(0); }}>Reset</button>
          <span className="px-2 py-1 text-slate-300">{Math.round(zoom * 100)}%</span>
        </div>
      </div>

      {/* RIGHT: controls. grid-column:2 pins the aside to the right
          column of the editor frame. Explicit width still set for
          old browsers / safety. */}
      <aside
        style={{ gridColumn: 2, gridRow: 1, width: 360 }}
        className="overflow-y-auto border-l border-slate-700 bg-slate-900 p-4 text-sm"
      >
        <header className="mb-3 flex items-start justify-between gap-2">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400">Fix georef</div>
            <h2 className="text-lg font-semibold">{airport} {procedureIdent ?? ''}</h2>
            <div className="text-xs text-slate-400">{pdfName} · cycle {cycle}</div>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-slate-300 hover:bg-slate-800 hover:text-white"
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        {courseDegTrue !== undefined && (
          <div className="mb-3 rounded border border-slate-700 bg-slate-800/40 p-2 text-xs text-slate-300">
            Approach course: <span className="font-mono text-white">{courseDegTrue.toFixed(1)}°T</span>
          </div>
        )}

        {/* Approach course-line tool */}
        <div className="mb-4 rounded border border-cyan-900/60 bg-cyan-950/30 p-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="font-medium text-cyan-200">Approach course arrow</span>
            <span className="text-xs text-cyan-300/70">
              {coursePoints.length === 2 ? 'drawn' : coursePoints.length === 1 ? '1/2' : 'not drawn'}
            </span>
          </div>
          <p className="mb-2 text-xs text-cyan-300/80">
            Draw the final approach course as a vector from the outer end
            into the airport. The arrowhead shows direction of flight. The
            arrow is used to sanity-check pin placements against the published
            FAC bearing.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setDrawingCourse(true);
                setCoursePoints([]);
                setPlacingWaypoint(null);
              }}
              className={`flex-1 rounded px-2 py-1 text-xs ${drawingCourse ? 'bg-cyan-500 text-white' : 'bg-cyan-900/60 text-cyan-100 hover:bg-cyan-800'}`}
            >
              {drawingCourse ? `Click point ${coursePoints.length + 1}/2` : 'Draw arrow'}
            </button>
            {coursePoints.length > 0 && (
              <button
                onClick={() => {
                  setCoursePoints([]);
                  setDrawingCourse(false);
                }}
                className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700"
              >
                Clear
              </button>
            )}
          </div>
          {facWarning && (
            <div className="mt-2 rounded border border-amber-700/60 bg-amber-950/30 p-2 text-[11px] leading-snug text-amber-100">
              ⚠️ {facWarning}
            </div>
          )}
        </div>

        {/* Airport override — shown when the plate has no airport ident
            attached. The cartographer can read the airport off the top
            of the plate and type it here; the wrapper reloads CIFP +
            NASR suggestions for that ident. (2026-05-18 boss request.) */}
        {airportUnknown && onAirportOverride && (
          <AirportOverridePanel
            current={airport === 'UNKN' ? null : airport}
            loading={fixesLoading}
            fixCount={fixes.length}
            onConfirm={onAirportOverride}
          />
        )}

        {/* Suggester banner — visible whenever we're working without a CIFP procedure. */}
        {bannerText && (
          <div className="mb-3 rounded border border-amber-700/60 bg-amber-950/30 p-2 text-xs text-amber-100">
            {bannerText}
          </div>
        )}

        {/* Tabbed waypoint checklist */}
        <div className="mb-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Waypoints ({anchors.length} placed)
          </h3>
          <div role="tablist" className="mb-2 flex flex-wrap gap-1 border-b border-slate-700">
            {tabConfigs.map((t) => {
              const count = fixesBySource[t.key].length;
              const active = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(t.key)}
                  className={[
                    'px-2 py-1 text-[11px] -mb-px border-b-2 whitespace-nowrap',
                    active
                      ? 'border-rose-500 text-white'
                      : 'border-transparent text-slate-400 hover:text-slate-200',
                  ].join(' ')}
                >
                  {t.label}
                  <span className="ml-1 font-mono text-[10px] text-slate-500">
                    ({count})
                  </span>
                </button>
              );
            })}
          </div>

          {activeTab === 'manual' ? (
            <>
              <ManualEntryPanel
                airport={airport}
                existing={annotatedFixes.map((f) => f.ident)}
                onAdd={(fix) => {
                  setManualFixes((prev) => {
                    if (prev.some((f) => f.ident === fix.ident)) return prev;
                    return [...prev, fix];
                  });
                }}
              />
              {/* (2026-05-18 boss report) Manual fixes the user just
                  typed were silently invisible on the Manual tab — the
                  list of placeable waypoints only showed when the user
                  switched away. Render them inline here so the user
                  sees the resolved coordinates + the Place button as
                  soon as the lookup succeeds. */}
              {fixesBySource.manual.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {fixesBySource.manual.map((f) => (
                    <FixRow
                      key={f.ident}
                      fix={f}
                      isPlacing={placingWaypoint === f.ident}
                      onPlace={() => {
                        setPlacingWaypoint(placingWaypoint === f.ident ? null : f.ident);
                        setDrawingCourse(false);
                      }}
                    />
                  ))}
                </ul>
              )}
            </>
          ) : fixesBySource[activeTab].length === 0 ? (
            <div className="rounded border border-slate-700 bg-slate-800/40 p-3 text-xs text-slate-400">
              {activeTab === 'cifp'
                ? 'No CIFP fixes available for this plate.'
                : activeTab === 'plate-text'
                  ? 'No idents were extracted from the plate text layer.'
                  : 'No nearby NASR waypoints within 25 NM of the airport.'}
            </div>
          ) : (
            <ul className="space-y-1">
              {fixesBySource[activeTab].map((f) => (
                <FixRow
                  key={f.ident}
                  fix={f}
                  isPlacing={placingWaypoint === f.ident}
                  onPlace={() => {
                    setPlacingWaypoint(placingWaypoint === f.ident ? null : f.ident);
                    setDrawingCourse(false);
                  }}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Solve + save */}
        <div className="mb-3 rounded border border-slate-700 bg-slate-800/40 p-2">
          {anchors.length < 2 && (
            <div className="text-xs text-amber-300">
              Place at least 2 anchors. Pin the airport (ARP) + one fix/navaid
              is the minimum; a 3rd pin improves robustness.
            </div>
          )}
          {anchors.length === 2 && (
            <div className="text-xs text-amber-300">
              2 anchors — fit will be deterministic but not robust. Add a 3rd
              if any is visible on the plate.
            </div>
          )}
          {anchors.length >= 3 && (
            <div className="text-xs text-emerald-300">
              {anchors.length} anchors ready. Solver will RANSAC + drop outliers.
            </div>
          )}
        </div>

        <button
          onClick={handleSave}
          disabled={!canSolve || saving}
          className={`mb-2 w-full rounded px-3 py-2 font-semibold ${
            canSolve && !saving
              ? 'bg-emerald-600 text-white hover:bg-emerald-500'
              : 'bg-slate-700 text-slate-400'
          }`}
        >
          {saving ? 'Saving…' : 'Save correction'}
        </button>

        {saveError && (
          <div className="mb-2 rounded border border-rose-700 bg-rose-950/40 p-2 text-xs text-rose-200">
            {saveError}
          </div>
        )}

        {savedOk && (
          <div className="mb-2 rounded border border-emerald-700 bg-emerald-950/40 p-2 text-xs">
            <div className="mb-1 text-lg font-semibold text-emerald-200">✅ Correction submitted for review</div>
            <p className="mb-2 text-emerald-300 leading-snug">
              You can see your own anchors on the chart right now. Other users
              won&apos;t see this correction until a Fyrefly cartographer approves it.
            </p>
            {solveResult && (
              <div className="mb-2 space-y-0.5 border-t border-emerald-800 pt-2 text-[10px] text-emerald-400">
                <div>Model: <span className="font-mono text-emerald-200">{solveResult.model}</span></div>
                <div>Residual: <span className="font-mono text-emerald-200">{solveResult.avgResidualNm.toFixed(4)} NM</span></div>
                <div>Matches: <span className="font-mono text-emerald-200">{solveResult.matchCount}</span></div>
              </div>
            )}
            <a
              href="/me/plate-corrections"
              onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/me/plate-corrections'; }}
              className="mt-1 inline-block rounded bg-slate-700 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-600"
            >
              📋 View my corrections
            </a>
          </div>
        )}

        {savedOk && (
          <button
            onClick={onClose}
            className="w-full rounded bg-emerald-700 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
          >
            Close editor
          </button>
        )}

        <div className="mt-3 text-[10px] text-slate-500">
          Right-click an anchor to remove. Mouse-wheel to zoom. Drag to pan
          (only when not placing). Anchors auto-snap to the course line when
          drawn (within {COURSE_SNAP_PX}px).
        </div>
      </aside>
    </div>,
    document.body,
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Fix row — single checklist entry with source-specific badge + label
// ──────────────────────────────────────────────────────────────────────────

function bearingCardinal(deg: number): string {
  const b = ((deg % 360) + 360) % 360;
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(b / 45) % 8];
}

function sourceBadge(f: EditorFix): { icon: string; tone: string } {
  switch (f.source) {
    case 'plate-text':       return { icon: '📡', tone: 'bg-sky-900/60 text-sky-200' };
    case 'geographic-nearby': return { icon: 'GEO', tone: 'bg-slate-700 text-slate-300' };
    case 'manual':           return { icon: '✎', tone: 'bg-violet-900/60 text-violet-200' };
    case 'cifp':
    default:                 return { icon: 'CIFP', tone: 'bg-emerald-900/60 text-emerald-200' };
  }
}

function FixRow({
  fix, isPlacing, onPlace,
}: {
  fix: EditorFix;
  isPlacing: boolean;
  onPlace: () => void;
}) {
  const placed = fix.placed;
  const badge = sourceBadge(fix);
  const muted = fix.source === 'geographic-nearby';
  const distLabel = fix.distanceFromArpNm != null && Number.isFinite(fix.distanceFromArpNm)
    ? `${fix.distanceFromArpNm.toFixed(1)} NM${fix.bearingFromArpDeg != null ? ` · ${bearingCardinal(fix.bearingFromArpDeg)}` : ''}`
    : null;
  const subline = [
    fix.navaidType,
    fix.name,
    fix.inferredRole && fix.inferredRole !== 'unknown' ? fix.inferredRole.toUpperCase() : null,
  ].filter(Boolean).join(' · ');

  return (
    <li
      className={`flex items-center justify-between rounded border px-2 py-1.5 ${
        isPlacing
          ? 'border-rose-500 bg-rose-950/50'
          : placed
            ? 'border-emerald-700 bg-emerald-950/30'
            : muted
              ? 'border-slate-800 bg-slate-900/40 opacity-90'
              : 'border-slate-700 bg-slate-800/40'
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`rounded px-1 py-0.5 text-[10px] font-semibold ${badge.tone}`}>
            {badge.icon}
          </span>
          <span className="font-mono font-medium">{fix.ident}</span>
          {fix.type && (
            <span className="rounded bg-slate-700 px-1 py-0.5 text-[10px] uppercase tracking-wide text-slate-300">
              {fix.type}
            </span>
          )}
          {placed && <span className="text-emerald-400">●</span>}
        </div>
        {subline && (
          <div className="truncate text-[11px] text-slate-300">{subline}</div>
        )}
        <div className="font-mono text-[10px] text-slate-400">
          {fix.lat.toFixed(5)}, {fix.lon.toFixed(5)}
          {distLabel && <span className="ml-2 text-slate-500">{distLabel}</span>}
        </div>
      </div>
      <button
        onClick={onPlace}
        className={`ml-2 rounded px-2 py-1 text-xs ${
          isPlacing
            ? 'bg-rose-500 text-white'
            : placed
              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
              : 'bg-rose-700 text-white hover:bg-rose-600'
        }`}
      >
        {isPlacing ? 'Click on chart…' : placed ? 'Move' : 'Place'}
      </button>
    </li>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Airport override — lets the cartographer assign an airport ident to a
// plate the upstream pipeline failed to identify. Validates via
// /api/nasr/lookup so we don't accept a typo. (2026-05-18 boss request.)
// ───────────────────────────────────────────────────────────────────────────

function AirportOverridePanel({
  current,
  loading,
  fixCount,
  onConfirm,
}: {
  current: string | null;
  loading?: boolean;
  fixCount: number;
  onConfirm: (ident: string) => void;
}) {
  const [ident, setIdent] = useState(current ?? '');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const submit = useCallback(async () => {
    const id = ident.trim().toUpperCase();
    if (!/^[A-Z][A-Z0-9]{2,3}$/.test(id)) {
      setStatus('error');
      setErrMsg('Enter a 3-4 character ICAO/FAA airport ident (e.g. KSIY or O46).');
      return;
    }
    setStatus('loading');
    setErrMsg(null);
    // Lightweight validation: hit the airport endpoint to confirm the
    // ident is a real airport before kicking off CIFP + suggester
    // re-fetches. /api/airport returns 404 for unknown idents.
    try {
      const resp = await fetch(`/api/airport?ident=${encodeURIComponent(id)}`);
      if (resp.status === 404) {
        setStatus('error');
        setErrMsg(`${id} isn't in our airport database. Double-check the ident.`);
        return;
      }
      if (!resp.ok) {
        setStatus('error');
        setErrMsg(`Lookup failed (HTTP ${resp.status}). Try again.`);
        return;
      }
      onConfirm(id);
      setStatus('idle');
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  }, [ident, onConfirm]);

  return (
    <div className="mb-3 rounded border border-sky-700/60 bg-sky-950/30 p-2 text-xs text-sky-100">
      <p className="mb-2">
        <span className="font-semibold">Airport unknown.</span> Read the airport name
        / ident off the top of the plate and enter it here so we can load CIFP
        fixes and nearby NASR waypoints.
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          value={ident}
          onChange={(e) => { setIdent(e.target.value.toUpperCase()); setStatus('idle'); }}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="e.g. KSIY"
          maxLength={4}
          className="flex-1 rounded border border-sky-700 bg-slate-900 px-2 py-1 font-mono uppercase placeholder:normal-case placeholder:text-slate-500"
        />
        <button
          onClick={submit}
          disabled={status === 'loading' || !ident.trim()}
          className="rounded bg-sky-700 px-3 py-1 text-white hover:bg-sky-600 disabled:bg-slate-700 disabled:text-slate-400"
        >
          {status === 'loading' ? 'Looking up…' : 'Set airport'}
        </button>
      </div>
      {status === 'error' && errMsg && (
        <p className="mt-2 text-rose-300">{errMsg}</p>
      )}
      {current && (
        loading ? (
          <p className="mt-2 text-sky-200">
            Loading CIFP procedures + nearby NASR waypoints for{' '}
            <span className="font-mono">{current}</span>…
          </p>
        ) : (
          <p className="mt-2 text-sky-200">
            Airport set to <span className="font-mono">{current}</span> · {fixCount} waypoint{fixCount === 1 ? '' : 's'} available below.
            {fixCount === 0 && ' (No CIFP fixes or nearby NASR waypoints; use Manual entry tab to add idents from the plate.)'}
          </p>
        )
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Manual entry — type an ident, hit /api/nasr/lookup, add to checklist
// ───────────────────────────────────────────────────────────────────────────

function ManualEntryPanel({
  airport, existing, onAdd,
}: {
  airport: string;
  existing: string[];
  onAdd: (fix: EditorFix) => void;
}) {
  const [ident, setIdent] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'added' | 'not-found' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [lastAdded, setLastAdded] = useState<string | null>(null);

  const existingSet = useMemo(() => new Set(existing.map((s) => s.toUpperCase())), [existing]);

  const doLookup = useCallback(async () => {
    const id = ident.trim().toUpperCase();
    if (!/^[A-Z][A-Z0-9]{2,4}$/.test(id)) {
      setStatus('error');
      setErrMsg('Enter a 3-5 character ident starting with a letter.');
      return;
    }
    if (existingSet.has(id)) {
      setStatus('error');
      setErrMsg(`${id} is already in the checklist.`);
      return;
    }
    setStatus('loading');
    setErrMsg(null);
    try {
      const url = new URL('/api/nasr/lookup', window.location.origin);
      url.searchParams.set('ident', id);
      if (airport) url.searchParams.set('airport', airport);
      const resp = await fetch(url.toString());
      if (resp.status === 404) {
        setStatus('not-found');
        return;
      }
      if (!resp.ok) {
        setStatus('error');
        setErrMsg(`HTTP ${resp.status}`);
        return;
      }
      const data = await resp.json() as {
        waypoint: {
          ident: string;
          origin: 'nasr-navaid' | 'nasr-fix';
          navaidType?: string;
          name: string;
          lat: number;
          lon: number;
          distanceFromArpNm: number;
          bearingFromArpDeg?: number;
        };
      };
      const w = data.waypoint;
      onAdd({
        ident: w.ident,
        lat: w.lat,
        lon: w.lon,
        source: 'manual',
        navaidType: w.navaidType,
        name: w.name,
        distanceFromArpNm: Number.isFinite(w.distanceFromArpNm) ? w.distanceFromArpNm : undefined,
        bearingFromArpDeg: w.bearingFromArpDeg,
        type: w.origin === 'nasr-navaid' ? 'navaid' : 'iaf',
      });
      setIdent('');
      // Surface a brief success line so the user knows the lookup
      // succeeded + the waypoint is now placeable in the list below.
      // (2026-05-18: boss reported manual entries felt like they were
      // dropped silently because the input cleared and nothing visible
      // changed in the Manual tab.)
      setStatus('added');
      setLastAdded(`${w.ident} · ${w.name}`);
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  }, [ident, existingSet, airport, onAdd]);

  return (
    <div className="rounded border border-slate-700 bg-slate-800/40 p-2 text-xs">
      <p className="mb-2 text-slate-300">
        Type an ident you can see on the plate. We'll look it up in NASR and add
        it to the checklist with its real lat/lon. If the ident isn't in NASR,
        place a custom anchor on the chart instead.
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          value={ident}
          onChange={(e) => { setIdent(e.target.value.toUpperCase()); setStatus('idle'); }}
          onKeyDown={(e) => { if (e.key === 'Enter') doLookup(); }}
          placeholder="e.g. OAL"
          maxLength={5}
          className="flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-1 font-mono uppercase placeholder:normal-case placeholder:text-slate-500"
        />
        <button
          onClick={doLookup}
          disabled={status === 'loading' || !ident.trim()}
          className="rounded bg-rose-700 px-3 py-1 text-white hover:bg-rose-600 disabled:bg-slate-700 disabled:text-slate-400"
        >
          {status === 'loading' ? 'Looking up…' : 'Add'}
        </button>
      </div>
      {status === 'added' && lastAdded && (
        <p className="mt-2 text-emerald-300">
          ✓ Added <span className="font-mono">{lastAdded}</span>. Use the
          Place button on the row below to mark its position on the chart.
        </p>
      )}
      {status === 'not-found' && (
        <p className="mt-2 text-amber-300">
          <span className="font-mono">{ident}</span> not found in NASR navaid or fix
          datasets — we don't have published GPS coordinates for it. This is
          rare for chart-named idents; double-check spelling, or look up a
          nearby NASR-known fix/navaid you can see on the same plate.
        </p>
      )}
      {status === 'error' && errMsg && (
        <p className="mt-2 text-rose-300">{errMsg}</p>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Geometric fingerprint — lightweight raster hash for cycle-transfer matching
// ──────────────────────────────────────────────────────────────────────────

/**
 * Cheap, deterministic fingerprint of the plate raster: downsample to a fixed
 * size (32×40 grid), threshold to ink-or-blank, hash the bitstring. NOT a
 * stroked-segment Hough hash (deferred per v1 scope) — but good enough to
 * detect "same plate, new cycle" when the FAA reuses the chart unchanged.
 */
async function computeRasterFingerprint(canvas: HTMLCanvasElement): Promise<string> {
  const GRID_W = 32;
  const GRID_H = 40;
  // Down-sample by drawing to a small offscreen canvas.
  const off = document.createElement('canvas');
  off.width = GRID_W;
  off.height = GRID_H;
  const ctx = off.getContext('2d');
  if (!ctx) throw new Error('fingerprint ctx unavailable');
  ctx.drawImage(canvas, 0, 0, GRID_W, GRID_H);
  const imgData = ctx.getImageData(0, 0, GRID_W, GRID_H);
  const data = imgData.data;
  // Threshold to ink-or-blank (luminance < 200 = ink)
  const bits = new Uint8Array(Math.ceil((GRID_W * GRID_H) / 8));
  for (let i = 0; i < GRID_W * GRID_H; i++) {
    const r = data[i * 4];
    const g = data[i * 4 + 1];
    const b = data[i * 4 + 2];
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    if (lum < 200) bits[Math.floor(i / 8)] |= 1 << (i % 8);
  }
  // SHA-256 hex
  const hashBuf = await crypto.subtle.digest('SHA-256', bits);
  const hashArr = Array.from(new Uint8Array(hashBuf));
  return hashArr.map((x) => x.toString(16).padStart(2, '0')).join('');
}
